import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MaterialReactTable } from 'material-react-table';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Snackbar } from '@mui/material';
import PatientSelection from './Frontend/1PatientSelection/PatientSelection';
import DoctorSelection from './Frontend/1.1DoctorSelection/DoctorSelection';
import MedicationSelection from './Frontend/2MedicationSelection/MedicationSelection';
import Configuration from './Frontend/3Configuration/Configuration';
import Signature from './Frontend/4Signature/Signature';
import FinalConfirmation from './Frontend/FinalConfirmation/FinalConfirmation';
import electronicPrescriptionService from './Service/ElectronicPrescriptionService';
import TopHeader from "../../components/common/Header/TopHeader";
import ElectronicPrescriptionPDF from './ElectronicPrescriptionPDF';

const ElectronicPrescription = () => {
    const location = useLocation();
    const [showPrescriptionsTable, setShowPrescriptionsTable] = useState(false);
    const [prescriptions, setPrescriptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [rowSelection, setRowSelection] = useState({});
    const [alert, setAlert] = useState({ open: false, message: '', severity: 'info' });
    const [showPDFModal, setShowPDFModal] = useState(false);
    const [selectedPrescriptionForPDF, setSelectedPrescriptionForPDF] = useState(null);

    // Estados para los datos de la prescripción
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [selectedMedication, setSelectedMedication] = useState(null);
    const [medicationDetails, setMedicationDetails] = useState(null);
    const [prescriptionConfig, setPrescriptionConfig] = useState(null);
    const [showFinalConfirmation, setShowFinalConfirmation] = useState(false);
    const [createdPrescriptionData, setCreatedPrescriptionData] = useState(null);
    const [errors, setErrors] = useState([]);
    const [success, setSuccess] = useState('');
    
    console.log('Estados actuales:', {
        selectedPatient,
        selectedDoctor,
        selectedMedication,
        medicationDetails,
        prescriptionConfig
    });

    // Datos del paciente recibidos desde la navegación
    const patientData = location.state ? {
        patientId: location.state.patientId,
        patientName: location.state.patientName,
        patientDob: location.state.patientDob,
        patientEmail: location.state.patientEmail,
        patientPhone: location.state.patientPhone
    } : null;

    // Configurar paciente inicial si viene de la navegación
    useEffect(() => {
        if (patientData && !selectedPatient) {
            setSelectedPatient({
                _id: patientData.patientId,
                name: patientData.patientName?.split(' ')[0] || '',
                surname: patientData.patientName?.split(' ').slice(1).join(' ') || '',
                dateOfBirth: patientData.patientDob,
                email: patientData.patientEmail,
                phone: patientData.patientPhone
            });
        }
    }, [patientData, selectedPatient]);

    // Configuración de columnas
    const columns = [
        {
            accessorKey: 'actions',
            header: 'Acciones',
            size: 100,
            enableSorting: false,
            enableColumnFilter: false,
            Cell: ({ row }) => {
                return (
                    <div className="d-flex gap-1">
                        <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            onClick={() => handleGeneratePDF(row.original)}
                            style={{
                                minWidth: 'auto',
                                padding: '4px 8px',
                                fontSize: '12px'
                            }}
                            title="Generar PDF de la receta"
                        >
                            📄 PDF
                        </Button>
                    </div>
                );
            },
        },
        {
            accessorKey: 'prescriptionId',
            header: 'ID Prescripción',
            size: 150,
            Cell: ({ row }) => {
                const id = row.original?.prescriptionId || row.original?._id || row.original?.id || 'N/A';
                return (
                    <span className="font-monospace small">
                        {id}
                    </span>
                );
            },
        },
        {
            accessorKey: 'patientInfo.fullName',
            header: 'Paciente',
            size: 200,
            Cell: ({ row }) => {
                const patientInfo = row.original?.patientInfo;
                let patientName = 'No disponible';
                
                if (patientInfo?.fullName) {
                    patientName = patientInfo.fullName;
                } else if (patientInfo?.userId?.name && patientInfo?.userId?.surname) {
                    patientName = `${patientInfo.userId.name} ${patientInfo.userId.surname}`;
                } else if (patientInfo?.userId?.name) {
                    patientName = patientInfo.userId.name;
                }
                
                return (
                    <div>
                        <div className="fw-medium">{patientName}</div>
                        {patientInfo?.idNumber && (
                            <div className="small text-muted">
                                {patientInfo.idType || 'DNI'}: {patientInfo.idNumber}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            accessorKey: 'medication.nombre',
            header: 'Medicamento',
            size: 300,
            Cell: ({ row }) => {
                const medication = row.original?.medication;
                const medicationName = medication?.nombre || 'No disponible';
                
                return (
                    <div>
                        <div className="fw-medium small">{medicationName}</div>
                        {medication?.codigoNacional && (
                            <div className="small text-muted">
                                Código: {medication.codigoNacional}
                            </div>
                        )}
                        {medication?.laboratorio && (
                            <div className="small text-primary">
                                {medication.laboratorio}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            accessorKey: 'medication.principioActivo',
            header: 'Principio Activo',
            size: 150,
            Cell: ({ row }) => {
                const activeIngredient = row.original?.medication?.principioActivo || 'No disponible';
                const dosisUnidad = row.original?.medication?.dosisUnidad;
                
                return (
                    <div>
                        <div className="fw-medium">{activeIngredient}</div>
                        {dosisUnidad && (
                            <div className="small text-muted">{dosisUnidad}</div>
                        )}
                    </div>
                );
            },
        },
        {
            accessorKey: 'status',
            header: 'Estado',
            size: 120,
            Cell: ({ cell }) => {
                const status = cell.getValue() || 'DESCONOCIDO';
                const readableStatus = electronicPrescriptionService.getReadableStatus(status);
                const badgeClasses = electronicPrescriptionService.getStatusBadge(status);
                
                return (
                    <span className={`badge ${badgeClasses}`}>
                        {readableStatus}
                    </span>
                );
            },
        },
        {
            accessorKey: 'createdAt',
            header: 'Fecha Creación',
            size: 150,
            Cell: ({ cell }) => {
                const date = cell.getValue();
                return date ? electronicPrescriptionService.formatDate(date) : 'No disponible';
            },
        },
        {
            accessorKey: 'expirationDate',
            header: 'Fecha Expiración',
            size: 150,
            Cell: ({ cell, row }) => {
                const expirationDate = cell.getValue() || 
                                      row.original?.dates?.expiration || 
                                      row.original?.expiresAt || 
                                      row.original?.validUntil;
                
                if (!expirationDate) return 'No disponible';
                
                const formattedDate = electronicPrescriptionService.formatDate(expirationDate);
                const daysUntilExpiration = electronicPrescriptionService.getDaysUntilExpiration(expirationDate);
                
                return (
                    <div>
                        <div>{formattedDate}</div>
                        {daysUntilExpiration !== null && (
                            <div className={`small ${daysUntilExpiration < 0 ? 'text-danger' : daysUntilExpiration <= 2 ? 'text-warning' : 'text-muted'}`}>
                                {daysUntilExpiration < 0 
                                    ? `Expiró hace ${Math.abs(daysUntilExpiration)} días`
                                    : daysUntilExpiration === 0
                                    ? 'Expira hoy'
                                    : `Expira en ${daysUntilExpiration} días`
                                }
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            accessorKey: 'numberOfPackages',
            header: 'Envases',
            size: 80,
            Cell: ({ cell }) => cell.getValue() || 1,
        },
        {
            accessorKey: 'treatmentDuration',
            header: 'Duración',
            size: 100,
            Cell: ({ row }) => {
                const duration = row.original?.treatmentDuration;
                if (!duration) return 'N/A';
                
                return `${duration.value || 0} ${duration.unit || 'DIAS'}`;
            },
        },
        {
            accessorKey: 'posology',
            header: 'Posología',
            size: 150,
            Cell: ({ row }) => {
                const posology = row.original?.posology;
                if (!posology) return 'No disponible';
                
                return (
                    <div className="small">
                        <div>{posology.cantidad || 'N/A'}</div>
                        <div className="text-muted">
                            {posology.frecuencia || 'N/A'}
                        </div>
                    </div>
                );
            },
        }
    ];

    const handleGeneratePDF = (prescription) => {
        console.log('Generando PDF para prescripción:', prescription);
        setSelectedPrescriptionForPDF(prescription);
        setShowPDFModal(true);
    };

    const loadPrescriptions = async () => {
        setIsLoading(true);
        try {
            let response;
            let prescriptionsData = [];
            
            console.log('🔍 Iniciando carga de prescripciones...');
            console.log('Selected patient:', selectedPatient);
            console.log('Patient data from navigation:', patientData);
            
            const hasSpecificPatient = selectedPatient?._id || patientData?.patientId;
            
            if (hasSpecificPatient) {
                const patientId = selectedPatient?._id || patientData?.patientId;
                console.log('👤 PACIENTE ESPECÍFICO: Cargando recetas para paciente:', patientId);
                
                try {
                    response = await electronicPrescriptionService.getElectronicPrescriptionsByPatient(patientId);
                    console.log('📋 Respuesta para paciente específico:', response);
                } catch (patientError) {
                    console.error('❌ Error al cargar recetas del paciente:', patientError);
                    throw new Error(`No se pudieron cargar las recetas del paciente: ${patientError.message}`);
                }
            } else {
                console.log('🌍 SIN PACIENTE ESPECÍFICO: Cargando todas las recetas disponibles');
                
                try {
                    response = await electronicPrescriptionService.getAllElectronicPrescriptions({
                        limit: 100,
                        sortBy: 'createdAt',
                        sortOrder: 'desc'
                    });
                    console.log('📊 Respuesta de todas las recetas:', response);
                } catch (allPrescriptionsError) {
                    console.log('⚠️ Fallo al obtener todas las recetas, intentando por doctor...');
                    
                    try {
                        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
                        if (currentUser?.id || currentUser?._id) {
                            const doctorId = currentUser.id || currentUser._id;
                            console.log('👨‍⚕️ Cargando recetas para doctor:', doctorId);
                            response = await electronicPrescriptionService.getElectronicPrescriptionsByDoctor(doctorId, {
                                limit: 100,
                                sortBy: 'createdAt',
                                sortOrder: 'desc'
                            });
                        } else {
                            throw new Error('No hay usuario autenticado para obtener recetas por doctor');
                        }
                    } catch (doctorError) {
                        console.log('⚠️ Fallo al obtener recetas por doctor, usando recetas próximas a expirar...');
                        response = await electronicPrescriptionService.getExpiringPrescriptions(30);
                        console.log('⏰ Respuesta de recetas próximas a expirar:', response);
                    }
                }
            }
            
            if (response) {
                console.log('✅ Respuesta completa recibida:', response);
                
                if (response.data?.electronicPrescriptions && Array.isArray(response.data.electronicPrescriptions)) {
                    prescriptionsData = response.data.electronicPrescriptions;
                } else if (response.electronicPrescriptions && Array.isArray(response.electronicPrescriptions)) {
                    prescriptionsData = response.electronicPrescriptions;
                } else if (response.data && Array.isArray(response.data)) {
                    prescriptionsData = response.data;
                } else if (response.prescriptions && Array.isArray(response.prescriptions)) {
                    prescriptionsData = response.prescriptions;
                } else if (Array.isArray(response)) {
                    prescriptionsData = response;
                } else if (response.results && Array.isArray(response.results)) {
                    prescriptionsData = response.results;
                } else {
                    console.log('⚠️ Formato de respuesta no reconocido:', response);
                    prescriptionsData = [];
                }
                
                prescriptionsData = prescriptionsData.map(prescription => ({
                    ...prescription,
                    id: prescription.prescriptionId || prescription._id || prescription.id,
                    expirationDate: prescription.dates?.expiration || prescription.expirationDate || prescription.validUntil
                }));
            }
            
            const contextMessage = hasSpecificPatient 
                ? `del paciente ${selectedPatient?.name || patientData?.patientName || 'seleccionado'}`
                : 'de todos los disponibles';
                
            console.log(`✅ ${prescriptionsData.length} recetas cargadas ${contextMessage}:`, prescriptionsData);
            
            if (prescriptionsData.length > 0) {
                console.log('📋 Primera prescripción procesada:', prescriptionsData[0]);
                console.log('📋 Estructura de medicamento:', prescriptionsData[0].medication);
                console.log('📋 Estructura de paciente:', prescriptionsData[0].patientInfo);
            }
            
            setPrescriptions(prescriptionsData);
            
            const alertMessage = hasSpecificPatient
                ? `Se cargaron ${prescriptionsData.length} recetas del paciente ${selectedPatient?.name || patientData?.patientName || 'seleccionado'}`
                : `Se cargaron ${prescriptionsData.length} recetas electrónicas`;
                
            setAlert({
                open: true,
                message: alertMessage,
                severity: prescriptionsData.length > 0 ? 'success' : 'info'
            });
            
        } catch (error) {
            console.error('❌ Error completo al cargar las recetas:', error);
            console.error('❌ Error stack:', error.stack);
            console.error('❌ Error response:', error.response?.data);
            
            const contextError = selectedPatient?._id || patientData?.patientId
                ? `Error al cargar las recetas del paciente: ${error.message}`
                : `Error al cargar las recetas: ${error.message}`;
                
            setAlert({
                open: true,
                message: contextError,
                severity: 'error'
            });
            
            const hasSpecificPatient = selectedPatient?._id || patientData?.patientId;
            const mockData = hasSpecificPatient ? [
                {
                    _id: 'mock_id_001',
                    id: 'MOCK_PATIENT_001',
                    prescriptionId: 'MPR25090516MOCK',
                    status: 'EMITIDA',
                    patientInfo: { 
                        fullName: selectedPatient?.name || patientData?.patientName || 'Paciente Seleccionado',
                        userId: { name: 'Rodrigo', surname: 'Donoso' }
                    },
                    medication: { 
                        nombre: 'ABACAVIR/LAMIVUDINA KERN PHARMA 600 MG/300 MG COMPRIMIDOS RECUBIERTOS CON PELICULA EFG', 
                        principioActivo: 'ABACAVIR/LAMIVUDINA',
                        codigoNacional: '84182'
                    },
                    createdAt: new Date().toISOString(),
                    dates: {
                        expiration: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
                    },
                    numberOfPackages: 1,
                    treatmentDuration: { value: 7, unit: 'DIAS' }
                }
            ] : [
                {
                    _id: 'mock_id_002',
                    id: 'MOCK_ALL_001',
                    prescriptionId: 'MPR25090516MOCK1',
                    status: 'EMITIDA',
                    patientInfo: { 
                        fullName: 'Juan Pérez García',
                        userId: { name: 'Juan', surname: 'Pérez García' }
                    },
                    medication: { 
                        nombre: 'ABACAVIR/LAMIVUDINA KERN PHARMA 600 MG/300 MG COMPRIMIDOS RECUBIERTOS CON PELICULA EFG', 
                        principioActivo: 'ABACAVIR/LAMIVUDINA',
                        codigoNacional: '84182'
                    },
                    createdAt: new Date().toISOString(),
                    dates: {
                        expiration: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
                    },
                    numberOfPackages: 1,
                    treatmentDuration: { value: 7, unit: 'DIAS' }
                },
                {
                    _id: 'mock_id_003',
                    id: 'MOCK_ALL_002',
                    prescriptionId: 'MPR25090516MOCK2',
                    status: 'BORRADOR',
                    patientInfo: { 
                        fullName: 'María González López',
                        userId: { name: 'María', surname: 'González López' }
                    },
                    medication: { 
                        nombre: 'ABACAVIR/LAMIVUDINA DR. REDDYS 600 MG/300 MG COMPRIMIDOS RECUBIERTOS CON PELICULA EFG', 
                        principioActivo: 'ABACAVIR/LAMIVUDINA',
                        codigoNacional: '81012'
                    },
                    createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
                    dates: {
                        expiration: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString()
                    },
                    numberOfPackages: 2,
                    treatmentDuration: { value: 7, unit: 'DIAS' }
                }
            ];
            
            setPrescriptions(mockData);
            console.log(`📊 Datos de ejemplo establecidos (${hasSpecificPatient ? 'paciente específico' : 'vista general'}):`, mockData);
            
        } finally {
            setIsLoading(false);
        }
    };   

    const handlePrescriptionCreated = (prescriptionData) => {
        if (prescriptionData?.success) {
            setCreatedPrescriptionData(prescriptionData);
            setShowFinalConfirmation(true);
            
            setAlert({
                open: true,
                message: 'Receta electrónica creada exitosamente',
                severity: 'success'
            });
            
            if (showPrescriptionsTable) {
                loadPrescriptions();
            }
        }
    };

    const handleResetToNewPrescription = () => {
        setShowFinalConfirmation(false);
        setCreatedPrescriptionData(null);
        setSelectedDoctor(null);
        setSelectedMedication(null);
        setMedicationDetails(null);
        setPrescriptionConfig(null);
        setErrors([]);
        setSuccess('');
        
        console.log('Reseteando componente para nueva prescripción');
    };

    useEffect(() => {
        if (showPrescriptionsTable) {
            loadPrescriptions();
        }
    }, [showPrescriptionsTable]);

    const handleCloseAlert = () => {
        setAlert({ ...alert, open: false });
    };

    return (
        <main id="app">
            <TopHeader />
            <div className="container my-4">
                <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
                    <h1 className="text-primary fw-semibold mb-0">Generación de Receta Electrónica</h1>
                    <div className="d-flex gap-2">
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setShowPrescriptionsTable(true)}
                        >
                            Ver Recetas Electrónicas
                        </Button>                        
                    </div>
                </div>

                {errors.length > 0 && (
                    <div className="alert alert-danger mb-4">
                        <h6 className="alert-heading">Errores encontrados:</h6>
                        <ul className="mb-0">
                            {errors.map((error, index) => (
                                <li key={index}>{String(error)}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {success && (
                    <div className="alert alert-success mb-4">
                        <strong>{success}</strong>
                    </div>
                )}

                {showFinalConfirmation ? (
                    <FinalConfirmation 
                        prescriptionData={createdPrescriptionData}
                        selectedPatient={selectedPatient}
                        onNewPrescription={handleResetToNewPrescription}
                        onShowPrescriptionsTable={() => setShowPrescriptionsTable(true)}
                    />
                ) : (
                    <>
                        {patientData && (
                            <div className="alert alert-info mb-4">
                                <h5 className="alert-heading">Paciente Seleccionado:</h5>
                                <p className="mb-1"><strong>Nombre:</strong> {patientData.patientName}</p>
                                <p className="mb-1"><strong>ID:</strong> {patientData.patientId}</p>
                                {patientData.patientEmail && (
                                    <p className="mb-1"><strong>Email:</strong> {patientData.patientEmail}</p>
                                )}
                                {patientData.patientPhone && (
                                    <p className="mb-0"><strong>Teléfono:</strong> {patientData.patientPhone}</p>
                                )}
                            </div>
                        )}

                        <PatientSelection 
                            initialPatientData={patientData}
                            onPatientSelect={setSelectedPatient}
                        />
                        
                        <DoctorSelection 
                            onDoctorSelect={setSelectedDoctor}
                            initialDoctorData={location.state?.doctorData || null}
                        />
                        
                        <MedicationSelection 
                            onMedicationSelect={setSelectedMedication}
                            onMedicationDetailsLoad={setMedicationDetails}
                        />
                        
                        <Configuration 
                            selectedMedication={selectedMedication}
                            medicationDetails={medicationDetails}
                            onConfigurationChange={setPrescriptionConfig}
                        />
                        
                        <Signature
                            selectedPatient={selectedPatient}
                            selectedDoctor={selectedDoctor}
                            selectedMedication={selectedMedication}
                            medicationDetails={medicationDetails}
                            prescriptionConfig={prescriptionConfig}
                            onPrescriptionCreated={handlePrescriptionCreated}
                        />
                    </>
                )}
            </div>

            <Dialog
                open={showPrescriptionsTable}
                onClose={() => setShowPrescriptionsTable(false)}
                maxWidth="xl"
                fullWidth
            >
                <DialogTitle>
                    {(() => {
                        const hasSpecificPatient = selectedPatient?._id || patientData?.patientId;
                        const patientName = selectedPatient?.name || patientData?.patientName;
                        
                        if (hasSpecificPatient) {
                            return `Recetas de ${patientName} (${prescriptions.length} registros)`;
                        } else {
                            return `Todas las Recetas Electrónicas (${prescriptions.length} registros)`;
                        }
                    })()}
                </DialogTitle>
                <DialogContent>
                    <div style={{ height: '600px', width: '100%' }}>
                        <MaterialReactTable
                            columns={columns}
                            data={prescriptions}
                            state={{ 
                                isLoading, 
                                rowSelection 
                            }}
                            onRowSelectionChange={setRowSelection}
                            enableRowSelection={true}
                            enableColumnFilters
                            enableColumnOrdering
                            enableSorting
                            enablePagination
                            initialState={{
                                pagination: { pageSize: 10, pageIndex: 0 },
                                sorting: [{ id: 'createdAt', desc: true }]
                            }}
                            renderTopToolbarCustomActions={() => (
                                <div className="d-flex gap-2">
                                    <Button
                                        variant="outlined"
                                        onClick={loadPrescriptions}
                                        disabled={isLoading}
                                    >
                                        🔄 Actualizar
                                    </Button>
                                    
                                    <Button
                                        variant={selectedPatient?._id || patientData?.patientId ? "contained" : "outlined"}
                                        color="primary"
                                        onClick={() => {
                                            if (selectedPatient?._id || patientData?.patientId) {
                                                setSelectedPatient(null);
                                                setAlert({
                                                    open: true,
                                                    message: 'Cambiando a vista general de todas las recetas',
                                                    severity: 'info'
                                                });
                                            } else {
                                                if (patientData) {
                                                    setSelectedPatient({
                                                        _id: patientData.patientId,
                                                        name: patientData.patientName?.split(' ')[0] || '',
                                                        surname: patientData.patientName?.split(' ').slice(1).join(' ') || ''
                                                    });
                                                }
                                                setAlert({
                                                    open: true,
                                                    message: 'Cambiando a vista de paciente específico',
                                                    severity: 'info'
                                                });
                                            }
                                            setTimeout(loadPrescriptions, 100);
                                        }}
                                    >
                                        {selectedPatient?._id || patientData?.patientId ? 
                                            "👥 Ver Todas" : 
                                            "👤 Ver Paciente"
                                        }
                                    </Button>
                                </div>
                            )}
                            muiTableBodyProps={{
                                sx: {
                                    '& .MuiTableRow-root': {
                                        '&:last-child td': { borderBottom: 0 }
                                    }
                                }
                            }}
                        />
                    </div>
                    {prescriptions.length === 0 && !isLoading && (
                        <div className="text-center py-4">
                            <p className="text-muted">
                                No se encontraron recetas electrónicas. 
                                {patientData ? ' Este paciente no tiene recetas registradas.' : ' Verifica la conexión con el servidor.'}
                            </p>
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowPrescriptionsTable(false)}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>

            {showPDFModal && selectedPrescriptionForPDF && (
                <ElectronicPrescriptionPDF
                    prescriptionData={selectedPrescriptionForPDF}
                    selectedPatient={{
                        _id: selectedPrescriptionForPDF.patientInfo?.userId?._id || selectedPrescriptionForPDF.patientInfo?.patientId,
                        name: selectedPrescriptionForPDF.patientInfo?.userId?.name || 
                              selectedPrescriptionForPDF.patientInfo?.fullName?.split(' ')[0] || '',
                        surname: selectedPrescriptionForPDF.patientInfo?.userId?.surname || 
                                 selectedPrescriptionForPDF.patientInfo?.fullName?.split(' ').slice(1).join(' ') || '',
                        dateOfBirth: selectedPrescriptionForPDF.patientInfo?.dateOfBirth || 
                                    selectedPrescriptionForPDF.patientInfo?.userId?.dateOfBirth,
                        idNumber: selectedPrescriptionForPDF.patientInfo?.idNumber || 
                                 selectedPrescriptionForPDF.patientInfo?.userId?.idNumber,
                        email: selectedPrescriptionForPDF.patientInfo?.email || 
                               selectedPrescriptionForPDF.patientInfo?.userId?.email,
                        phone: selectedPrescriptionForPDF.patientInfo?.phone || 
                               selectedPrescriptionForPDF.patientInfo?.userId?.phone
                    }}
                    selectedDoctor={{
                        _id: selectedPrescriptionForPDF.prescriberInfo?.userId?._id || selectedPrescriptionForPDF.prescriberInfo?.doctorId,
                        name: selectedPrescriptionForPDF.prescriberInfo?.userId?.name || 
                              selectedPrescriptionForPDF.prescriberInfo?.fullName?.split(' ')[0] || '',
                        surname: selectedPrescriptionForPDF.prescriberInfo?.userId?.surname || 
                                 selectedPrescriptionForPDF.prescriberInfo?.fullName?.split(' ').slice(1).join(' ') || '',
                        collegiateNumber: selectedPrescriptionForPDF.prescriberInfo?.collegiateNumber,
                        specialty: selectedPrescriptionForPDF.prescriberInfo?.specialty
                    }}
                    medicationDetails={selectedPrescriptionForPDF.medication}
                    prescriptionConfig={{
                        posology: selectedPrescriptionForPDF.posology,
                        treatmentDuration: selectedPrescriptionForPDF.treatmentDuration,
                        numberOfPackages: selectedPrescriptionForPDF.numberOfPackages,
                        specialInstructions: selectedPrescriptionForPDF.specialInstructions
                    }}
                    onClose={() => {
                        setShowPDFModal(false);
                        setSelectedPrescriptionForPDF(null);
                    }}
                />
            )}

            <Snackbar
                open={alert.open}
                autoHideDuration={6000}
                onClose={handleCloseAlert}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseAlert}
                    severity={alert.severity}
                    variant="filled"
                >
                    {alert.message}
                </Alert>
            </Snackbar>
        </main>
    );
};

export default ElectronicPrescription;